/**
 * MonitoredTenantsTab — the Monitored Tenants pane of the `/admin` portal.
 *
 * Composition mirrors OrganizationsTab + PeopleTab (Carbon, mockup 02):
 *   1. A "Monitored tenants" DataTable whose status tabs (FilterChipRow:
 *      All / Active / Needs consent / Inactive) own the filter key. "Needs
 *      consent" uses named-state voice, never "PendingConsent".
 *   2. When `orgFilter` arrives from the Organizations tab: an info notice
 *      in the table. TenantDto carries NO `organizationId` today (the
 *      Container `/tenantmanagement` endpoint never returns the linked Org),
 *      so it says the filter can't apply, with a Clear action, rather than
 *      fabricating a join. See TenantDrawerContent's file-level note for the
 *      same caveat on the Org value.
 *   3. Columns: Name (domain below), Auth mode, Consent status, Last scan,
 *      Open. No Org column: the linkage data is missing. A row click or
 *      Enter also opens the drawer.
 *   4. AdminDrawer + TenantDrawerContent: opens when `state.drawer` starts
 *      with `tenant:`. The drawer body owns the consent/edit/deactivate
 *      flows and reaches back through this tab for the eight mutation
 *      handles `useTenants` exposes.
 *   5. "Add tenant", the view's one primary button, sits in the table's
 *      toolbar and opens `TenantFormModal` in create mode. Edit mode is
 *      launched from inside the drawer.
 *
 * State ownership:
 *   - Tab-local filter, drawer id, and cross-tab pivots live in the URL via
 *     `useAdminPortalState`. Deep links + browser back work for free.
 *   - Data + mutations come from `useTenants`. `pendingConsentCount` is the
 *     canonical derivation used by the chip count AND the AdminPortal score
 *     box — both readers settle on the same TanStack cache entry.
 *   - No `<h1>` — the AdminPortalHeader (one level up) carries the verdict.
 *
 * Voice:
 *   - "Needs consent" status tab: named state, not "PendingConsent". In a
 *     row it is the strong neutral tag: the one state that needs an admin.
 *   - Per-tenant tenants read "Per-tenant credentials" in the consent column.
 *   - The empty row is one sentence that names the way out.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { useTenants, type TenantDto } from '@/hooks/useTenants'
import { useAdminPortalState } from '@/hooks/useAdminPortalState'
import { lastAssessmentAt } from '@/lib/tenant-assessed'

import { Button } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { IconAdd } from '@/components/carbon/icons'
import { Tag } from '@/components/carbon/Tag'
import FilterChipRow, { type FilterChip } from '@/components/AdminPortal/FilterChipRow'
import AdminDrawer from '@/components/AdminPortal/AdminDrawer'
import TenantDrawerContent from '@/components/AdminPortal/TenantDrawerContent'
import TenantFormModal from '@/components/AdminPortal/TenantFormModal'

/* -------------------------------------------------------------------------- */
/* Filter helpers                                                             */
/* -------------------------------------------------------------------------- */

type FilterKey = 'all' | 'active' | 'needs-consent' | 'inactive'

const VALID_FILTERS: ReadonlySet<string> = new Set([
  'all',
  'active',
  'needs-consent',
  'inactive',
])

function parseFilter(raw: string | null): FilterKey {
  return raw && VALID_FILTERS.has(raw) ? (raw as FilterKey) : 'all'
}

function matchesFilter(t: TenantDto, key: FilterKey): boolean {
  if (key === 'all') return true
  if (key === 'active') return t.isActive === true
  // "Needs consent" mirrors `derivePendingConsentCount` exactly so the chip
  // count and the filtered row set agree.
  if (key === 'needs-consent') {
    return t.authenticationMode === 'MultiTenant' && !t.adminConsentGrantedAt
  }
  return t.isActive === false
}

/* -------------------------------------------------------------------------- */
/* Drawer id helpers                                                          */
/* -------------------------------------------------------------------------- */

const TENANT_DRAWER_PREFIX = 'tenant:'

function parseTenantDrawer(drawer: string | null): string | null {
  if (!drawer || !drawer.startsWith(TENANT_DRAWER_PREFIX)) return null
  return drawer.slice(TENANT_DRAWER_PREFIX.length)
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function MonitoredTenantsTab() {
  const state = useAdminPortalState()
  const {
    tenants,
    isLoading,
    isError,
    error,
    pendingConsentCount,
    onboardTenant,
    updateTenant,
    deactivateTenant,
    testTenantAuth,
    initiateAdminConsent,
    verifyAndRecordConsent,
    revokeAdminConsent,
    sendOnboardingEmail,
  } = useTenants()

  // Local-only state: "Add tenant" modal visibility. Not URL-bound because
  // it's transient and never deep-linked. The Edit flow is owned by the
  // drawer (TenantDrawerContent mounts its own TenantFormModal instance).
  const [showAddModal, setShowAddModal] = useState(false)

  /* ── Filter + counts ─────────────────────────────────────────────────── */

  const activeFilter = parseFilter(state.filter)

  const counts = useMemo(
    () => ({
      all: tenants.length,
      active: tenants.filter(t => t.isActive === true).length,
      // pendingConsentCount is the canonical derivation — the score box and
      // the nav badge use the same value.
      'needs-consent': pendingConsentCount,
      inactive: tenants.filter(t => t.isActive === false).length,
    }),
    [tenants, pendingConsentCount],
  )

  const filteredTenants = useMemo(
    () => tenants.filter(t => matchesFilter(t, activeFilter)),
    [tenants, activeFilter],
  )

  const chips: FilterChip[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'needs-consent', label: 'Needs consent', count: counts['needs-consent'] },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
  ]

  /* ── Drawer resolution ───────────────────────────────────────────────── */

  const drawerTenantId = parseTenantDrawer(state.drawer)
  const drawerTenant = useMemo(
    () => (drawerTenantId ? tenants.find(t => t.tenantId === drawerTenantId) ?? null : null),
    [tenants, drawerTenantId],
  )

  /* ── Render ──────────────────────────────────────────────────────────── */

  const filterLabel = chips.find(c => c.key === activeFilter)?.label.toLowerCase() ?? 'all'

  return (
    <div className="flex flex-col gap-8">
      {isError && (
        <InlineNotification kind="error" title="Tenants did not load.">
          {error?.message ?? 'Reload the page to try again.'}
        </InlineNotification>
      )}

      <TenantsTable
        rows={filteredTenants}
        isLoading={isLoading && tenants.length === 0}
        tabs={
          <FilterChipRow
            chips={chips}
            active={activeFilter}
            onChange={key => state.setFilter(key === 'all' ? null : key)}
            ariaLabel="Filter tenants by status"
          />
        }
        notices={
          /*
            Cross-tab pivot notice. TenantDto has NO `organizationId`, so we
            can't actually scope the list by org today: say so honestly
            instead of silently dropping the filter. When the wire shape grows
            the linkage, swap this for the filter tag PeopleTab uses.
          */
          state.orgFilter ? (
            <InlineNotification
              kind="info"
              title="Tenants are not linked to organizations yet."
              actions={
                <Button kind="ghost" size="sm" onClick={() => state.setOrgFilter(null)}>
                  Clear the filter
                </Button>
              }
            >
              This list shows every tenant.
            </InlineNotification>
          ) : undefined
        }
        toolbar={
          <div className="ml-auto flex">
            <Button kind="primary" icon={<IconAdd />} onClick={() => setShowAddModal(true)}>
              Add tenant
            </Button>
          </div>
        }
        empty={
          tenants.length === 0
            ? isError
              ? 'The list did not load.'
              : 'No tenant is monitored yet. Add the first one above.'
            : `No ${filterLabel} tenants. Choose another status to see the rest.`
        }
        onOpen={t => state.setDrawer(`${TENANT_DRAWER_PREFIX}${t.tenantId}`)}
      />

      <TenantDrawer
        tenant={drawerTenant}
        onClose={() => state.setDrawer(null)}
        onPivot={(_target, orgId) =>
          state.mergePivot({ tab: 'organizations', orgFilter: orgId, filter: null })
        }
        onboardTenant={onboardTenant}
        updateTenant={updateTenant}
        deactivateTenant={deactivateTenant}
        testTenantAuth={testTenantAuth}
        initiateAdminConsent={initiateAdminConsent}
        verifyAndRecordConsent={verifyAndRecordConsent}
        revokeAdminConsent={revokeAdminConsent}
        sendOnboardingEmail={sendOnboardingEmail}
      />

      {showAddModal && (
        <TenantFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          editingTenant={null}
          onSuccess={() => setShowAddModal(false)}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

interface TenantsTableProps {
  rows: TenantDto[]
  onOpen(t: TenantDto): void
  tabs?: React.ReactNode
  notices?: React.ReactNode
  toolbar?: React.ReactNode
  isLoading?: boolean
  empty?: React.ReactNode
}

function TenantsTable({ rows, onOpen, tabs, notices, toolbar, isLoading, empty }: TenantsTableProps) {
  const columns: DataColumn<TenantDto>[] = [
    {
      id: 'name',
      header: 'Name',
      primary: true,
      cell: t => <TableItem title={t.name} sub={t.domain} />,
    },
    { id: 'auth', header: 'Auth mode', inlineOnPhone: true, cell: t => authModeLabel(t.authenticationMode) },
    { id: 'consent', header: 'Consent status', inlineOnPhone: true, cell: t => <ConsentStatusCell tenant={t} /> },
    { id: 'scan', header: 'Last scan', inlineOnPhone: true, cell: t => <LastScanCell tenant={t} /> },
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      align: 'end',
      cell: t => (
        <Button kind="ghost" size="sm" onClick={() => onOpen(t)} aria-label={`Open ${t.name}`}>
          Open
        </Button>
      ),
    },
  ]

  return (
    <DataTable
      title="Monitored tenants"
      description="The Microsoft 365 tenants Guardian watches, how it signs in to each, and when it last scanned them."
      columns={columns}
      rows={rows}
      rowId={t => t.id}
      rowLabel={t => t.name}
      tabs={tabs}
      notices={notices}
      toolbar={toolbar}
      isLoading={isLoading}
      onRowActivate={onOpen}
      empty={empty}
    />
  )
}

function authModeLabel(mode: TenantDto['authenticationMode']): React.ReactNode {
  if (mode === 'MultiTenant') return 'Multi-tenant'
  if (mode === 'PerTenant') return 'Per-tenant'
  return <span className="text-text-helper">Not set</span>
}

function ConsentStatusCell({ tenant }: { tenant: TenantDto }) {
  if (tenant.authenticationMode !== 'MultiTenant') {
    return <span className="text-text-helper">Per-tenant credentials</span>
  }
  if (tenant.adminConsentGrantedAt) {
    const granted = formatDate(tenant.adminConsentGrantedAt)
    return <>{granted ? `Granted ${granted}` : 'Granted'}</>
  }
  // The one state here that needs an admin: the strong neutral, no hue.
  return <Tag kind="high-contrast">Needs consent</Tag>
}

function LastScanCell({ tenant }: { tenant: TenantDto }) {
  // `lastScanAt` is the creation stamp, or the .NET DateTime.MinValue
  // sentinel, until the first scan; neither is a scan date.
  const iso = lastAssessmentAt(tenant)
  const label = iso ? formatDate(iso) : null
  return label ? <>{label}</> : <span className="text-text-helper">Not scanned yet</span>
}

/* -------------------------------------------------------------------------- */
/* Drawer wrapper                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Wraps AdminDrawer + TenantDrawerContent and keeps the last tenant painted
 * through the slide-out (mirrors PeopleTab's `PersonDrawer`). `isOpen`
 * drives the transition; the captured ref keeps the body rendered while
 * the URL has already cleared.
 */
function TenantDrawer({
  tenant,
  onClose,
  onPivot,
  onboardTenant,
  updateTenant,
  deactivateTenant,
  testTenantAuth,
  initiateAdminConsent,
  verifyAndRecordConsent,
  revokeAdminConsent,
  sendOnboardingEmail,
}: {
  tenant: TenantDto | null
  onClose(): void
  onPivot: React.ComponentProps<typeof TenantDrawerContent>['onPivot']
  onboardTenant: React.ComponentProps<typeof TenantDrawerContent>['onboardTenant']
  updateTenant: React.ComponentProps<typeof TenantDrawerContent>['updateTenant']
  deactivateTenant: React.ComponentProps<typeof TenantDrawerContent>['deactivateTenant']
  testTenantAuth: React.ComponentProps<typeof TenantDrawerContent>['testTenantAuth']
  initiateAdminConsent: React.ComponentProps<typeof TenantDrawerContent>['initiateAdminConsent']
  verifyAndRecordConsent: React.ComponentProps<typeof TenantDrawerContent>['verifyAndRecordConsent']
  revokeAdminConsent: React.ComponentProps<typeof TenantDrawerContent>['revokeAdminConsent']
  sendOnboardingEmail: React.ComponentProps<typeof TenantDrawerContent>['sendOnboardingEmail']
}) {
  const lastTenantRef = useRef<TenantDto | null>(null)
  useEffect(() => {
    if (tenant) lastTenantRef.current = tenant
  }, [tenant])

  const displayTenant = tenant ?? lastTenantRef.current
  if (!displayTenant) return null

  const subtitleSuffix = displayTenant.domain ?? displayTenant.tenantId.slice(0, 8)
  const subtitle = `Tenant · ${subtitleSuffix}`

  return (
    <AdminDrawer
      isOpen={tenant !== null}
      onClose={onClose}
      title={displayTenant.name}
      subtitle={subtitle}
    >
      <TenantDrawerContent
        tenant={displayTenant}
        onClose={onClose}
        onPivot={onPivot}
        onboardTenant={onboardTenant}
        updateTenant={updateTenant}
        deactivateTenant={deactivateTenant}
        testTenantAuth={testTenantAuth}
        initiateAdminConsent={initiateAdminConsent}
        verifyAndRecordConsent={verifyAndRecordConsent}
        revokeAdminConsent={revokeAdminConsent}
        sendOnboardingEmail={sendOnboardingEmail}
      />
    </AdminDrawer>
  )
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
